import {
  ChangeLog,
  ActivityItem,
  customAttributeFieldNames,
  dateFieldNames,
  statusUpdateFields,
} from "components/activity-logs/activity-log/definitions";
import { isGaiaObjectResource } from "utils/gaia-constants";
import {
  CHILD_OBJECTS,
  CORE_OBJECTS,
  childScanFlag,
} from "components/team_settings/scans-enabled/definitions";
import {
  AUDIT_FIELDS,
  AuditFieldKey,
  ESCALATION_FIELD_MAPPINGS,
  EscalationFieldKey,
  GAIA_OBJECT_FIELDS,
  GaiaObjectFieldKey,
  GENERAL_FIELD_MAPPINGS,
  GeneralFieldKey,
  PAYMENT_ASSESSMENT_FIELD_MAPPINGS,
  PaymentAssessmentFieldKey,
  REVIEW_FIELD_MAPPINGS,
  ReviewFieldKey,
  TASK_FIELD_MAPPINGS,
  TaskFieldKey,
  TEAM_CONFIG_CATEGORIES,
  TEAM_CONFIG_ITEM_FIELD_MAPPINGS,
  TEAM_LEVEL_TEAM_FIELDS,
  TEAM_FIELD_MAPPINGS,
  TeamConfigItemFieldKey,
  TeamFieldKey,
} from "./field-mappings";

export const isDateField = (attributeName: string): boolean => {
  const cleanName = attributeName.replace("custom_", "");

  return (
    dateFieldNames.includes(cleanName) ||
    cleanName.endsWith("Date") ||
    cleanName.endsWith("_date")
  );
};

export const parseCustomAttributes = (
  value: string,
): Record<string, string> => {
  if (!value) return {};
  // Parse the format: {key1=value1, key2=value2, ...}
  const cleanValue = value.replace(/^{|}$/g, ""); // Remove outer braces
  if (!cleanValue) return {};

  const result: Record<string, string> = {};
  const regex = /(\w+)=([^,]*(?:,[^,]*)*?)(?=,\s*\w+=|$)/g;

  let match;
  while ((match = regex.exec(cleanValue)) !== null) {
    const key = match[1].trim();
    const value = match[2].trim();
    if (key && value !== undefined) {
      result[key] = value;
    }
  }

  return result;
};

/**
 * Checks if a change log represents a status-related change
 * Handles both direct status fields and fields in custom attributes data map
 */
export const isStatusRelatedChange = (
  change: ChangeLog,
  statusUpdateFields: string[],
): boolean => {
  if (statusUpdateFields.includes(change.attributeName)) {
    return true;
  }

  if (customAttributeFieldNames.includes(change.attributeName)) {
    const oldAttributes = parseCustomAttributes(change.oldValue);
    const newAttributes = parseCustomAttributes(change.newValue);

    return statusUpdateFields.some(
      (field) => oldAttributes[field] !== newAttributes[field],
    );
  }

  return false;
};

/**
 * Pretty-print a raw string that is expected to hold JSON. Returns a
 * multi-line JSON representation when parseable, or the raw string verbatim
 * on any parse failure (Java `Map.toString()` / `List.toString()` shapes,
 * empty strings, etc.). Never throws.
 */
export const prettyPrintTeamJson = (raw: string): string => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

/**
 * Prettifies a camelCase string into a human-readable label.
 * Handles acronyms correctly by keeping consecutive uppercase letters together
 * and restoring known acronyms to uppercase.
 * Example: "taskGroupName" → "Task group name", "atlasKBName" → "Atlas KB name"
 */
const KNOWN_ACRONYMS = ["id", "kb", "url"];

export const prettifyCamelCase = (fieldName: string): string => {
  return fieldName
    .replace(/([A-Z]+)([A-Z][a-z0-9])/g, "$1 $2")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase()
    .replace(/^./, (str) => str.toUpperCase())
    .replace(new RegExp(`\\b(${KNOWN_ACRONYMS.join("|")})\\b`, "gi"), (match) =>
      match.toUpperCase(),
    );
};

export const getUIFieldName = (
  dbFieldName: string,
  category: string,
): string => {
  // Ahead of the custom_ branch: closing requirements store a real "custom_requirements" field.
  const teamConfigItemLabel =
    TEAM_CONFIG_ITEM_FIELD_MAPPINGS[dbFieldName as TeamConfigItemFieldKey];
  if (teamConfigItemLabel && TEAM_CONFIG_CATEGORIES.has(category)) {
    return teamConfigItemLabel;
  }
  if (dbFieldName.startsWith("custom_")) {
    const cleanFieldName = dbFieldName.replace("custom_", "");
    // Check if it's a known gaia object field first
    if (GAIA_OBJECT_FIELDS[cleanFieldName as GaiaObjectFieldKey]) {
      return GAIA_OBJECT_FIELDS[cleanFieldName as GaiaObjectFieldKey];
    }
    // Otherwise format it nicely
    return prettifyCamelCase(cleanFieldName);
  }

  const snakeToCamel = (str: string) =>
    str
      .toLowerCase()
      .replace(/([-_][a-z])/g, (group) =>
        group.toUpperCase().replace("-", "").replace("_", ""),
      );

  // Check gaiaobject_ prefixed fields against GAIA_OBJECT_FIELDS directly
  if (dbFieldName.startsWith("gaiaobject_")) {
    if (GAIA_OBJECT_FIELDS[dbFieldName as GaiaObjectFieldKey]) {
      return GAIA_OBJECT_FIELDS[dbFieldName as GaiaObjectFieldKey];
    }
    // Strip prefix and prettify the remainder for unmapped gaiaobject_ fields
    return prettifyCamelCase(
      snakeToCamel(dbFieldName.replace("gaiaobject_", "")),
    );
  }
  const camelCaseField = snakeToCamel(dbFieldName);
  const generalMapping = (fieldName: string): string => {
    return (
      GENERAL_FIELD_MAPPINGS[dbFieldName as GeneralFieldKey] ||
      GENERAL_FIELD_MAPPINGS[fieldName as GeneralFieldKey] ||
      prettifyCamelCase(fieldName)
    );
  };

  switch (category) {
    case "Task details":
      return (
        TASK_FIELD_MAPPINGS[camelCaseField as TaskFieldKey] ||
        generalMapping(camelCaseField)
      );
    case "Progress update":
    case "Audit details":
      return (
        AUDIT_FIELDS[camelCaseField as AuditFieldKey] ||
        generalMapping(camelCaseField)
      );
    case "Notice details":
    case "Litigation details":
      // Check direct field mapping first
      if (GAIA_OBJECT_FIELDS[dbFieldName as GaiaObjectFieldKey]) {
        return GAIA_OBJECT_FIELDS[dbFieldName as GaiaObjectFieldKey];
      }
      // Then check camelCase version
      return (
        GAIA_OBJECT_FIELDS[camelCaseField as GaiaObjectFieldKey] ||
        generalMapping(camelCaseField)
      );
    case "Payment & Assessment":
      return (
        PAYMENT_ASSESSMENT_FIELD_MAPPINGS[
          camelCaseField as PaymentAssessmentFieldKey
        ] || generalMapping(camelCaseField)
      );
    case "Reviews":
      return (
        REVIEW_FIELD_MAPPINGS[dbFieldName as ReviewFieldKey] ||
        generalMapping(camelCaseField)
      );
    case "Escalation":
      return (
        ESCALATION_FIELD_MAPPINGS[dbFieldName as EscalationFieldKey] ||
        generalMapping(camelCaseField)
      );
    case "Team config":
      return (
        TEAM_FIELD_MAPPINGS[dbFieldName as TeamFieldKey] ||
        generalMapping(camelCaseField)
      );
    default:
      return generalMapping(camelCaseField);
  }
};

export interface ReopenEntry {
  reason: string;
  reopenedBy: string;
  reopenedAt: string;
}

export const parseReopenHistory = (
  oldValue: string,
  newValue: string,
): ReopenEntry | null => {
  const parseEntries = (value: string): Array<Record<string, string>> => {
    if (!value || value === "[]") return [];
    // Format: [{reason=x, reopenedBy=y, reopenedAt=z}, {...}]
    const entries: Array<Record<string, string>> = [];
    const entryRegex = /\{([^}]+)\}/g;
    let match;
    while ((match = entryRegex.exec(value)) !== null) {
      const entry: Record<string, string> = {};
      const parts = match[1].split(/,\s*(?=\w+=)/);
      parts.forEach((part) => {
        const eqIndex = part.indexOf("=");
        if (eqIndex > 0) {
          const key = part.substring(0, eqIndex).trim();
          const val = part.substring(eqIndex + 1).trim();
          entry[key] = val;
        }
      });
      entries.push(entry);
    }
    return entries;
  };

  const newEntries = parseEntries(newValue);
  if (newEntries.length === 0) return null;

  const oldEntries = parseEntries(oldValue);

  const latestEntry = newEntries[newEntries.length - 1];
  if (
    newEntries.length > oldEntries.length &&
    latestEntry.reason !== undefined
  ) {
    return {
      reason: latestEntry.reason || "",
      reopenedBy: latestEntry.reopenedBy || "",
      reopenedAt: latestEntry.reopenedAt || "",
    };
  }

  return null;
};

/** Strict progress-update test for the activity-log Category column. */
export const isProgressUpdate = (activity: ActivityItem): boolean => {
  const { resourceId, operation, changeLogs } = activity;
  if (!isGaiaObjectResource(resourceId)) {
    return false;
  }
  // Creates arrive with a seeded status change, so guard on operation.
  if (operation === "create") {
    return false;
  }
  return changeLogs.some((change) =>
    isStatusRelatedChange(change, statusUpdateFields),
  );
};

/** Create-inclusive variant for the progress-update history table. */
export const isProgressHistoryEntry = (activity: ActivityItem): boolean =>
  activity.operation === "create"
    ? isGaiaObjectResource(activity.resourceId)
    : isProgressUpdate(activity);

const TEAM_TOGGLEABLE_SCAN_FLAGS = new Set(
  CORE_OBJECTS.flatMap((core) =>
    CHILD_OBJECTS.map((child) => childScanFlag(core.prefix, child.key)),
  ),
);

// Flag values arrive as JSON ('["A","B"]') or Java toString ("[A, B]"); tokens cover both.
const parseFeatureFlags = (value: string): Set<string> =>
  new Set(value?.match(/[A-Z][A-Z0-9_]*/g) ?? []);

/** A team record change the team could make: reopen window, payment configuration or scan toggles. */
export const isTeamLevelChange = (change: ChangeLog): boolean => {
  if (!TEAM_LEVEL_TEAM_FIELDS.has(change.attributeName)) return false;
  if (change.attributeName !== "enabled_features") return true;
  const before = parseFeatureFlags(change.oldValue);
  const after = parseFeatureFlags(change.newValue);
  const changed = [...before, ...after].filter(
    (flag) => before.has(flag) !== after.has(flag),
  );
  return (
    changed.length > 0 &&
    changed.every((flag) => TEAM_TOGGLEABLE_SCAN_FLAGS.has(flag))
  );
};
